/**
 *
 * AppAuthenticatorsLayout
 *
 */

import React, { useEffect } from "react";
import classnames from "classnames";
import { stylize } from "@envestnet/envreact-component-library";
import withIntl from "utils/withIntl";

import CentricLayout from "components/CentricLayout";

import { useLocation } from "react-router-dom";

import DashboardMfaLayout from "components/DashboardMfaLayout";
import { mfaFlow } from "utils/enum";
import AppAuth from "./AppAuth";

import styles, { Styles } from "./styles";

import { StatusType, SubmissionStateType } from "./types";
import { AuthenticatorStatusEnum, PassCodeSubmissionStatusEnum } from "./enums";
import messages from "./messages";

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below

  /** Description of this prop */

  goToMFAScreen: Function;
  loadQRCode: Function;
  submitPassCode: Function;
  loadStatus: Function;
  reset: Function;
  resetSubmissionStatus: Function;
  loadingPassCodeSubmission: SubmissionStateType;
  qrCodeLink: string;
  status: StatusType;
  secretCode: string;
  username: string;
}

export interface AppAuthenticatorsLayoutProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;

  goToMFAScreen: Function;
}

const defaultProps: DefaultProps = {
  goToMFAScreen: () => {},
  loadQRCode: () => {},
  submitPassCode: () => {},
  loadStatus: () => {},
  reset: () => {},
  resetSubmissionStatus: () => {},
  qrCodeLink: "",
  loadingPassCodeSubmission: PassCodeSubmissionStatusEnum.INITIATE,
  status: AuthenticatorStatusEnum.INITIAL,
  secretCode: "",
  username: "",
};

type Props = InjectedProps & AppAuthenticatorsLayoutProps;

const AppAuthenticatorsLayout = (props: Props) => {
  const {
    classes,
    style,
    className,
    innerRef,
    goToMFAScreen,
    loadQRCode,
    qrCodeLink,
    submitPassCode,
    loadingPassCodeSubmission,
    loadStatus,
    status,
    reset,
    secretCode,
    resetSubmissionStatus,
    username,
    formatMessage,
  } = props;
  const [activeStep, setActiveStep] = React.useState(1);
  const { state } = useLocation();

  const redirectToMfa = () => {
    goToMFAScreen(state.navigationFrom);
  };

  useEffect(() => {
    if (loadingPassCodeSubmission === PassCodeSubmissionStatusEnum.DONE) {
      redirectToMfa();
    }
  }, [loadingPassCodeSubmission]);

  useEffect(() => {
    if (status === AuthenticatorStatusEnum.PENDING) setActiveStep(2);
    else if (status === AuthenticatorStatusEnum.VERIFIED) {
      redirectToMfa();
    }
  }, [status]);

  useEffect(
    () => () => {
      reset();
    },
    []
  );

  return state?.currentLayout === mfaFlow.MFA ? (
    // @ts-ignore
    <DashboardMfaLayout>
      <CentricLayout className={classes.mfaCenter}>
        <AppAuth
          hideLogo
          secretCode={secretCode}
          resetSubmissionStatus={resetSubmissionStatus}
          username={username}
          loadQRCode={loadQRCode}
          currentLayout={state?.currentLayout}
          qrCodeLink={qrCodeLink}
          submitPassCode={submitPassCode}
          loadingPassCodeSubmission={loadingPassCodeSubmission}
          loadStatus={loadStatus}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          redirectToMfa={redirectToMfa}
          authenticatorName={formatMessage(messages.header)}
        />
      </CentricLayout>
    </DashboardMfaLayout>
  ) : (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <CentricLayout>
        <div>
          <AppAuth
            hideLogo={false}
            secretCode={secretCode}
            resetSubmissionStatus={resetSubmissionStatus}
            username={username}
            loadQRCode={loadQRCode}
            currentLayout={state?.currentLayout}
            qrCodeLink={qrCodeLink}
            submitPassCode={submitPassCode}
            loadingPassCodeSubmission={loadingPassCodeSubmission}
            loadStatus={loadStatus}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            redirectToMfa={redirectToMfa}
            authenticatorName={formatMessage(messages.header)}
          />
        </div>
      </CentricLayout>
    </div>
  );
};

AppAuthenticatorsLayout.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(AppAuthenticatorsLayout)
);
